import { useState, type ChangeEvent } from "react";
import { cn } from "@/utils/classNames";
import { Accordion, AccordionContent, AccordionItem } from "@/components/ui/accordion";
import { FiClock, FiCalendar, FiEdit2, FiTrash2 } from "react-icons/fi";

type Day = {
  name: string;
  active: boolean;
};

type ScheduleOverviewPanelProps = {
  schedule: {
    id: string;
    name: string;
    time: string;
    days: Day[];
    enabled: boolean;
  };
  // 关联设备（如 UV、Pressure Cleaner）名称列表
  linkedDevices?: string[];
  onEnabledChange?: (enabled: boolean) => void;
  onEdit?: () => void;
  onDelete?: () => void;
};

export function SchedulePanelOverview({ schedule, linkedDevices, onEnabledChange, onEdit, onDelete }: ScheduleOverviewPanelProps) {
  const [enabled, setEnabled] = useState<boolean>(schedule.enabled);

  const handleToggleEnabled = (e: ChangeEvent<HTMLInputElement>) => {
    const next = e.target.checked;
    setEnabled(next);
    onEnabledChange?.(next);
  };

  const handleEdit = () => {
    onEdit?.();
  };

  const handleDelete = () => {
    onDelete?.();
  };

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200 flex flex-col h-full">
      <Accordion type="single" collapsible defaultValue={schedule.id} className="flex-1">
        <AccordionItem value={schedule.id} className="border-0">
          <div className="flex items-center justify-between px-4 py-3 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200">
            <div className="flex items-center gap-3">
              <div className={cn(linkedDevices && linkedDevices.length > 0 ? "min-h-[70px]" : "min-h-[50px]")}> 
                <div className="flex items-center gap-2">
                  <div
                    className={cn(
                      "w-3 h-3 rounded-full transition-colors duration-200",
                      enabled ? "bg-green-500" : "bg-gray-300 dark:bg-gray-500"
                    )}
                  />
                  <h5 className="text-base font-medium text-gray-800 dark:text-gray-200">{schedule.name}</h5>
                </div>
                {/* 关联设备名称展示 */}
                {linkedDevices && linkedDevices.length > 0 && (
                  <div className="mt-1 text-sm text-gray-700 dark:text-gray-300 space-y-0.5">
                    {linkedDevices.map((name) => (
                      <div key={name}>{name}</div>
                    ))}
                  </div>
                )}
                <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                  <div className="flex items-center gap-1">
                    <FiClock className="h-3.5 w-3.5 text-gray-600 dark:text-gray-400" />
                    <span>{schedule.time}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <FiCalendar className="h-3.5 w-3.5 text-gray-600 dark:text-gray-400" />
                    <span>{schedule.days.filter((d) => d.active).length} days</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3 ml-auto">
              <button
                className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"
                aria-label="Edit schedule"
                onClick={handleEdit}
              >
                <FiEdit2 className="h-4 w-4" />
              </button>

              <button
                className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-red-600 dark:text-red-400"
                aria-label="Delete schedule"
                onClick={handleDelete}
              >
                <FiTrash2 className="h-4 w-4" />
              </button>

              <input
                type="checkbox"
                aria-label="Enable schedule"
                className="rounded border-gray-300 dark:border-gray-600 ml-1"
                checked={enabled}
                onChange={handleToggleEnabled}
              />
            </div>
          </div>

          {linkedDevices && linkedDevices.length > 0 && (
            <AccordionContent className="px-4 pb-4 pt-2 bg-white dark:bg-gray-800"></AccordionContent>
          )}
        </AccordionItem>
      </Accordion>
      {/* 固定在卡片底部的 Active Days */}
      <div className="px-4 pb-4 pt-2 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 mt-auto">
        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Active Days</p>
          <div className="flex gap-2">
            {schedule.days.map((day) => (
              <div
                key={day.name}
                className={cn(
                  "w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all duration-200",
                  day.active
                    ? "bg-cyan-500 text-white shadow-sm"
                    : "bg-gray-100 text-gray-500 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                )}
              >
                {day.name.substring(0, 1)}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );}